import React, { useEffect } from 'react';
import { Download, ExternalLink, Mic, MessageSquare, Users, Briefcase, GraduationCap, Shield, Star, Clock } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageSEO from '../components/PageSEO';
import './PressPage.css';

const BRAND_COLORS = [
  { group: "Primary Colors", colors: [
    { name: "Primary Navy", hex: "#1B2A4E", rgb: "rgb(27, 42, 78)", desc: "Trust, Expertise, Stability", light: true },
    { name: "White", hex: "#FFFFFF", rgb: "rgb(255, 255, 255)", desc: "Clarity, Openness", light: false },
    { name: "Primary Gold", hex: "#C9A227", rgb: "rgb(201, 162, 39)", desc: "Excellence, Achievement", light: true },
  ]},
  { group: "Secondary & Neutral", colors: [
    { name: "Slate Blue", hex: "#5A6C8A", rgb: "rgb(90, 108, 138)", desc: "Professional, Modern", light: true },
    { name: "Light Gray", hex: "#E8E8E8", rgb: "rgb(232, 232, 232)", desc: "Neutrality, Balance", light: false },
    { name: "Charcoal", hex: "#333333", rgb: "rgb(51, 51, 51)", desc: "Focus, Strength", light: true },
    { name: "Off White", hex: "#FAFAFA", rgb: "rgb(250, 250, 250)", desc: "Sophistication, Subtlety", light: false },
  ]},
];

const R6_DIMS = [
  { num: "01", name: "People", sub: "Precision Hiring", desc: "Talent acquisition, employer branding, and leadership assessment.", icon: Users },
  { num: "02", name: "Roles", sub: "Clear Architecture", desc: "Organizational design, job architecture, and talent infrastructure.", icon: Briefcase },
  { num: "03", name: "Skills", sub: "Future Ready", desc: "Executive coaching, leadership development, and performance management.", icon: GraduationCap },
  { num: "04", name: "Environment", sub: "Culture Performs", desc: "Culture strategy, employee relations, and workforce engagement.", icon: Shield },
  { num: "05", name: "Incentives", sub: "Rewards Inspire", desc: "Compensation, total rewards design, and recognition systems.", icon: Star },
  { num: "06", name: "Time", sub: "Anticipate Needs", desc: "Succession planning, talent analytics, and compliance management.", icon: Clock },
];

const SPEAKING_TOPICS = [
  { title: "The Right 6\u2122 Framework", desc: "How six interconnected talent dimensions drive organizational excellence and sustainable growth." },
  { title: "Talent Issues Are Business Issues", desc: "Why people strategy must be central to business strategy \u2014 and how to make it happen." },
  { title: "Building Enterprise-Level HR for Growing Organizations", desc: "How small and midsized businesses, franchises, and nonprofits can access Fortune 500-caliber talent solutions." },
  { title: "From Reactive to Strategic", desc: "Transforming people decisions from firefighting to future-proofing your organization." },
  { title: "The Future of Workforce Development", desc: "AI-powered learning, personalized career pathways, and the changing landscape of employee development." },
  { title: "Culture as a Competitive Advantage", desc: "Building high-performance cultures that attract, retain, and inspire top talent." },
];

const PODCAST_TOPICS = [
  { title: "Scaling People Operations", desc: "Lessons from building HR infrastructure at fast-growing organizations." },
  { title: "The Talent War for Small & Midsized Business", desc: "How smaller organizations can compete for talent against larger competitors." },
  { title: "Succession Planning That Actually Works", desc: "Moving beyond check-the-box succession to strategic talent pipelines." },
  { title: "Compensation Strategy in a Changing Economy", desc: "Designing total rewards that attract talent without breaking the budget." },
  { title: "Community-Driven Workforce Development", desc: "Building talent ecosystems that benefit both employers and communities." },
  { title: "The Role of AI in Modern HR", desc: "Practical applications of AI in recruiting, learning, and talent analytics." },
];

const DOWNLOADABLE_ASSETS = [
  { title: "R6 Collective Logo (Stacked)", desc: "Primary stacked logo with wordmark — digital and print use", type: "WEBP", size: "35.5 KB", src: "https://customer-assets-0z36b82j.emergentagent.net/job_18d5f296-1985-4248-97b7-ff15c2909e3a/artifacts/00cwtguq_The%20R6%20Collective_v2.webp" },
  { title: "The Right Path Logo", desc: "The Right Path program logo with R6 Collective branding", type: "WEBP", size: "69.1 KB", src: "https://customer-assets-0z36b82j.emergentagent.net/job_18d5f296-1985-4248-97b7-ff15c2909e3a/artifacts/43ip0kfk_The%20Right%20Path_Logo_v.webp" },
  { title: "R6 Box Logo", desc: "Compact box logo mark for icons and favicons", type: "JPG", size: "13.8 KB", src: "https://customer-assets.emergentagent.com/job_collective-build-1/artifacts/ev3zutd7_R6%20Box%20Logo%20Only%20.jpg" },
  { title: "Team Collaboration Image", desc: "Professional team meeting image", type: "JPG", size: "268 KB", src: "https://customer-assets.emergentagent.com/job_collective-build-1/artifacts/n2ooixlp_iStock-1151863802.jpg" },
  { title: "Business Strategy Image", desc: "Strategic planning visualization", type: "JPG", size: "303 KB", src: "https://customer-assets.emergentagent.com/job_collective-build-1/artifacts/i8zwnurb_iStock-2190572410.jpg" },
  { title: "Professional Workspace Image", desc: "Modern office environment", type: "JPG", size: "231 KB", src: "https://customer-assets.emergentagent.com/job_collective-build-1/artifacts/x1oho0fp_iStock-2203784055.jpg" },
];

const FONT_HIERARCHY = [
  { label: "Heading H1", spec: "Montserrat Bold \u00b7 48pt \u00b7 #1B2A4E", size: "2rem", weight: 700, color: "#1B2A4E" },
  { label: "Subheading H2", spec: "Montserrat SemiBold \u00b7 36pt \u00b7 #1B2A4E", size: "1.5rem", weight: 600, color: "#1B2A4E" },
  { label: "Subheading H3", spec: "Montserrat SemiBold \u00b7 24pt \u00b7 #C9A227", size: "1.15rem", weight: 600, color: "#C9A227" },
  { label: "Body Text", spec: "Montserrat Regular \u00b7 16pt \u00b7 #1B2A4E", size: "0.95rem", weight: 400, color: "#1B2A4E" },
  { label: "Tagline & Accents", spec: "Montserrat Medium \u00b7 14pt \u00b7 #C9A227", size: "0.82rem", weight: 500, color: "#C9A227" },
];

const JOTFORM_URL = "https://form.jotform.com/260186646305054";

const PressPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="pk-page" data-testid="press-page">
      <PageSEO
        title="Press Kit"
        path="/press"
        description="Press kit for The R6 Collective. Download logos, brand assets, speaking topics, and media resources. Everything you need to tell The R6 Collective story."
      />
      <Header />

      {/* ─── HERO ─── */}
      <section className="pk-hero" data-testid="pk-hero">
        <div className="pk-hero-inner">
          <span className="pk-label-hero">Press Kit</span>
          <h1 className="pk-hero-title">Media Resources.</h1>
          <p className="pk-hero-sub">
            Everything you need to tell The R6&#8482; Collective story. Download logos, brand assets, and key information.
          </p>
          <div className="pk-hero-ctas">
            <a href={JOTFORM_URL} target="_blank" rel="noopener noreferrer" className="pk-btn pk-btn-gold" data-testid="pk-media-inquiries">
              Media Inquiries <ExternalLink size={14} />
            </a>
            <a href="#downloads" className="pk-btn pk-btn-outline" data-testid="pk-download-kit">
              Download Press Kit
            </a>
          </div>
        </div>
      </section>

      {/* ─── ABOUT ─── */}
      <section className="pk-section" data-testid="pk-about">
        <div className="pk-container">
          <span className="pk-label">About The R6&#8482; Collective</span>
          <div className="pk-about-text">
            <p>
              The R6&#8482; Collective was founded on a simple premise: all organizations deserve access to
              comprehensive talent solutions — from foundational to strategic talent expertise.
            </p>
            <p>
              Our team brings decades of combined experience from leading organizations across diverse
              sectors — from Fortune 100+, small and midsized businesses and franchises to civic organizations
              and nonprofits. We've built, scaled, and optimized complete talent systems for organizations at
              every stage of growth.
            </p>
            <p>
              The Right 6&#8482; Framework represents the synthesis of best practices and proven methodologies.
              It's a comprehensive approach designed for organizations ready to build agile and strategic
              talent solutions that drives their mission forward.
            </p>
          </div>
        </div>
      </section>

      {/* ─── BRAND COLORS ─── */}
      <section className="pk-section pk-section-alt" data-testid="pk-colors">
        <div className="pk-container">
          <span className="pk-label">Brand Colors</span>
          <h2 className="pk-title">The R6 palette.</h2>
          {BRAND_COLORS.map((group) => (
            <div key={group.group} className="pk-color-group">
              <h3 className="pk-color-group-title">{group.group}</h3>
              <div className="pk-color-grid">
                {group.colors.map((c) => (
                  <div key={c.hex} className="pk-color-card" data-testid={`color-${c.name.replace(/\s/g, '-').toLowerCase()}`}>
                    <div className="pk-color-swatch" style={{ background: c.hex, border: c.hex === '#FFFFFF' || c.hex === '#FAFAFA' || c.hex === '#E8E8E8' ? '1px solid #E5E7EB' : 'none' }}>
                      {c.light && <span className="pk-swatch-label" style={{ color: 'white' }}>{c.name}</span>}
                      {!c.light && <span className="pk-swatch-label" style={{ color: '#1B2A4E' }}>{c.name}</span>}
                    </div>
                    <div className="pk-color-info">
                      <span className="pk-color-hex">HEX: {c.hex}</span>
                      <span className="pk-color-rgb">{c.rgb}</span>
                      <span className="pk-color-desc">{c.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── TYPOGRAPHY ─── */}
      <section className="pk-section" data-testid="pk-typography">
        <div className="pk-container">
          <span className="pk-label">Typography</span>
          <h2 className="pk-title">Montserrat. All text.</h2>
          <div className="pk-type-showcase">
            <div className="pk-type-specimen">
              <span className="pk-type-meta">Primary Font Family — All Text</span>
              <p className="pk-type-upper">ABCDEFGHIJKLMNOPQRSTUVWXYZ</p>
              <p className="pk-type-lower">abcdefghijklmnopqrstuvwxyz</p>
              <p className="pk-type-nums">0123456789</p>
              <a href="https://fonts.google.com/specimen/Montserrat" target="_blank" rel="noopener noreferrer" className="pk-type-link">
                Download from Google Fonts <ExternalLink size={13} />
              </a>
            </div>
            <div className="pk-type-hierarchy">
              <h3 className="pk-type-hier-title">Font Hierarchy</h3>
              {FONT_HIERARCHY.map((f, i) => (
                <div key={i} className="pk-type-row">
                  <span className="pk-type-sample" style={{ fontSize: f.size, fontWeight: f.weight, color: f.color }}>
                    {f.label}
                  </span>
                  <span className="pk-type-spec">{f.spec}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── R6 FRAMEWORK ─── */}
      <section className="pk-section pk-section-alt" data-testid="pk-framework">
        <div className="pk-container">
          <span className="pk-label">The Right 6&#8482; Framework</span>
          <h2 className="pk-title">Six interconnected dimensions that define organizational talent excellence.</h2>
          <div className="pk-r6-grid">
            {R6_DIMS.map((d) => (
              <div key={d.num} className="pk-r6-card" data-testid={`r6-dim-${d.num}`}>
                <span className="pk-r6-num">{d.num}</span>
                <d.icon size={22} className="pk-r6-icon" />
                <h4>{d.name}</h4>
                <span className="pk-r6-sub">{d.sub}</span>
                <p>{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── KEY STATISTICS ─── */}
      <section className="pk-section pk-section-stats" data-testid="pk-stats">
        <div className="pk-container">
          <div className="pk-stats-row">
            <div className="pk-stat">
              <span className="pk-stat-num">6</span>
              <span className="pk-stat-label">Critical Talent Dimensions</span>
            </div>
            <div className="pk-stat">
              <span className="pk-stat-num">100%</span>
              <span className="pk-stat-label">Strategic Focus</span>
            </div>
            <div className="pk-stat">
              <span className="pk-stat-num">100+</span>
              <span className="pk-stat-label">Years Combined Executive Experience</span>
            </div>
          </div>
        </div>
      </section>

      {/* ─── SPEAKING TOPICS ─── */}
      <section className="pk-section" data-testid="pk-speaking">
        <div className="pk-container">
          <span className="pk-label">Speaking Topics</span>
          <h2 className="pk-title">Available for keynotes, panels, workshops, and executive briefings.</h2>
          <div className="pk-topics-grid">
            {SPEAKING_TOPICS.map((t, i) => (
              <div key={i} className="pk-topic-card" data-testid={`speaking-${i}`}>
                <Mic size={18} className="pk-topic-icon" />
                <h4>{t.title}</h4>
                <p>{t.desc}</p>
              </div>
            ))}
          </div>
          <p className="pk-topics-note">
            We also offer additional speaking topics tailored to your organization's specific needs.
            Contact us to discuss custom presentations.
          </p>
        </div>
      </section>

      {/* ─── PODCAST & INTERVIEW TOPICS ─── */}
      <section className="pk-section pk-section-alt" data-testid="pk-podcast">
        <div className="pk-container">
          <span className="pk-label">Podcast & Interview Topics</span>
          <h2 className="pk-title">Looking for expert guests? Our team is available.</h2>
          <div className="pk-topics-grid">
            {PODCAST_TOPICS.map((t, i) => (
              <div key={i} className="pk-topic-card" data-testid={`podcast-${i}`}>
                <MessageSquare size={18} className="pk-topic-icon" />
                <h4>{t.title}</h4>
                <p>{t.desc}</p>
              </div>
            ))}
          </div>
          <p className="pk-topics-note">
            Have a specific topic in mind? We're happy to discuss additional subjects based on your audience's interests and needs.
          </p>
          <a href={JOTFORM_URL} target="_blank" rel="noopener noreferrer" className="pk-btn pk-btn-navy" data-testid="pk-book-speaker">
            Book a Speaker or Interview <ExternalLink size={14} />
          </a>
        </div>
      </section>

      {/* ─── DOWNLOADABLE ASSETS ─── */}
      <section className="pk-section" id="downloads" data-testid="pk-downloads">
        <div className="pk-container">
          <span className="pk-label">Downloadable Assets</span>
          <h2 className="pk-title">Logos & imagery. Ready to use.</h2>
          <div className="pk-assets-grid">
            {DOWNLOADABLE_ASSETS.map((a, i) => (
              <div key={i} className="pk-asset-card" data-testid={`asset-${i}`}>
                <div className="pk-asset-preview">
                  <img src={a.src} alt={a.title} loading="lazy" />
                </div>
                <div className="pk-asset-info">
                  <h4>{a.title}</h4>
                  <p>{a.desc}</p>
                  <span className="pk-asset-meta">{a.type} &middot; {a.size}</span>
                </div>
                <a href={a.src} download className="pk-btn pk-btn-dl" target="_blank" rel="noopener noreferrer" data-testid={`download-asset-${i}`}>
                  <Download size={16} /> Download
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── MEDIA CONTACT ─── */}
      <section className="pk-section pk-section-cta" data-testid="pk-contact">
        <div className="pk-container pk-contact-inner">
          <span className="pk-label pk-label-light">Media Contact</span>
          <h2>For press inquiries, interviews, or additional information.</h2>
          <a href={JOTFORM_URL} target="_blank" rel="noopener noreferrer" className="pk-btn pk-btn-gold" data-testid="pk-contact-btn">
            Get in Touch <ExternalLink size={14} />
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PressPage;
